export const WIDGET_ISSUE_SEVERITY = {
    error: 'error',
    warning: 'warning',
    info: 'info',
} as const;
export type WidgetIssueSeverity = typeof WIDGET_ISSUE_SEVERITY[keyof typeof WIDGET_ISSUE_SEVERITY];

export const WIDGET_ISSUE_SEVERITY_ORDER: WidgetIssueSeverity[] = [
    WIDGET_ISSUE_SEVERITY.error,
    WIDGET_ISSUE_SEVERITY.warning,
    WIDGET_ISSUE_SEVERITY.info,
];

export interface WidgetIssue {
    severity: WidgetIssueSeverity;
    title: string;
    body: string;
    fix: string | null;
}

const WARNING_TYPES: ReadonlySet<string> = new Set(['WARNING', 'WARN']);
const INFO_TYPES: ReadonlySet<string> = new Set(['INFO', 'INFORMATION', 'NOTICE']);

export const toWidgetIssueSeverity = (raw?: string | null): WidgetIssueSeverity => {
    const normalized = (raw ?? '').trim().toUpperCase();
    if (WARNING_TYPES.has(normalized)) return WIDGET_ISSUE_SEVERITY.warning;
    if (INFO_TYPES.has(normalized)) return WIDGET_ISSUE_SEVERITY.info;
    return WIDGET_ISSUE_SEVERITY.error;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

const parseWidgetIssue = (raw: unknown): WidgetIssue | null => {
    if (!isRecord(raw)) return null;
    const title = typeof raw.title === 'string' ? raw.title : '';
    const body = typeof raw.body === 'string' ? raw.body : '';
    if (!title && !body) return null;

    const fix = typeof raw.fix === 'string' ? raw.fix : typeof raw.how_to_fix === 'string' ? raw.how_to_fix : null;

    return {
        severity: toWidgetIssueSeverity(typeof raw.type === 'string' ? raw.type : null),
        title,
        body,
        fix,
    };
};

export const parseWidgetIssues = (raw: unknown): WidgetIssue[] => {
    if (!Array.isArray(raw)) return [];
    return raw.map(parseWidgetIssue).filter((issue): issue is WidgetIssue => issue !== null);
};

export const countIssuesBySeverity = (issues: WidgetIssue[]): Record<WidgetIssueSeverity, number> =>
    issues.reduce<Record<WidgetIssueSeverity, number>>(
        (acc, issue) => {
            acc[issue.severity] += 1;
            return acc;
        },
        { [WIDGET_ISSUE_SEVERITY.error]: 0, [WIDGET_ISSUE_SEVERITY.warning]: 0, [WIDGET_ISSUE_SEVERITY.info]: 0 }
    );

export const sortWidgetIssues = (issues: WidgetIssue[]): WidgetIssue[] =>
    [...issues].sort((a, b) => WIDGET_ISSUE_SEVERITY_ORDER.indexOf(a.severity) - WIDGET_ISSUE_SEVERITY_ORDER.indexOf(b.severity));
